import { useMemo, useRef, useState } from 'react'
import { formatMoney, formatMoneyCompact } from '@najah/core'
import { cn } from '@najah/ui'

export interface LineSeries {
  key: string
  label: string
  /** لون من رموز الرسم المُتحقَّق منها: var(--chart-1) أو var(--chart-2) */
  color: string
  values: number[]
}

interface Props {
  labels: string[]
  series: LineSeries[]
  height?: number
  /** تنسيق القيمة في التلميح والمحور */
  format?: (v: number) => string
  /** صيغة علامات المحور — مختصرة افتراضياً حتى لا تُقصّ */
  tickFormat?: (v: number) => string
  emptyText?: string
}

// اليسار يتّسع لعلامة مختصرة (‏136.5 ألف) لا لمبلغ كامل — المبلغ الكامل يُقصّ
const PAD = { top: 16, right: 16, bottom: 28, left: 56 }

/** يقرّب الحدّ الأعلى إلى رقم نظيف حتى تكون علامات المحور مقروءة */
function niceCeil(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10
  return step * magnitude
}

/**
 * رسم خطّي للسلاسل الزمنية.
 *
 * الاتجاه معكوس عمداً: الأقدم يميناً والأحدث يساراً، ليسير الزمن مع اتجاه
 * القراءة العربية. نافذة الرسم نفسها لا تنعكس بـ dir لأن SVG لا يعكس
 * إحداثياته — نعكس ترتيب البيانات بدل ذلك.
 */
export function LineChart({
  labels, series, height = 260,
  format = formatMoney, tickFormat = formatMoneyCompact,
  emptyText = 'لا توجد بيانات',
}: Props) {
  const [hover, setHover] = useState<number | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const width = 720

  const { points, ticks } = useMemo(() => {
    const all = series.flatMap((s) => s.values)
    const rawMax = all.length ? Math.max(...all) : 0
    const max = niceCeil(rawMax || 1)

    const innerW = width - PAD.left - PAD.right
    const innerH = height - PAD.top - PAD.bottom
    const n = labels.length

    // النقطة i تُرسم من اليمين: i=0 أقصى اليمين
    const xOf = (i: number) => PAD.left + innerW - (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW)
    const yOf = (v: number) => PAD.top + innerH - (v / max) * innerH

    const points = series.map((s) => ({
      ...s,
      coords: s.values.map((v, i) => ({ x: xOf(i), y: yOf(v), v })),
    }))

    const ticks = Array.from({ length: 5 }, (_, i) => {
      const v = (max / 4) * i
      return { v, y: yOf(v) }
    })

    return { max, points, ticks }
  }, [labels, series, height])

  if (!labels.length) {
    return (
      <div className="grid h-[260px] place-items-center rounded-lg border border-dashed border-border text-sm text-fg-muted">
        {emptyText}
      </div>
    )
  }

  const innerW = width - PAD.left - PAD.right
  const n = labels.length

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = ((e.clientX - rect.left) / rect.width) * width
    // عكس الإسقاط: من الإحداثي إلى فهرس النقطة
    const ratio = (PAD.left + innerW - x) / innerW
    const index = Math.round(ratio * (n - 1))
    setHover(index >= 0 && index < n ? index : null)
  }

  return (
    <div className="relative">
      {/* وسيلة الإيضاح حاضرة دائماً مع سلسلتين أو أكثر */}
      {series.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-4">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-xs text-fg-muted">
              <span className="h-0.5 w-4 rounded-full" style={{ background: s.color }} aria-hidden />
              {s.label}
            </span>
          ))}
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="w-full touch-none"
        style={{ height }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={`رسم بياني: ${series.map((s) => s.label).join(' و')}`}
      >
        {/* الشبكة: خطوط شعرية صلبة، متراجعة بصرياً */}
        {ticks.map((t) => (
          <g key={t.v}>
            <line
              x1={PAD.left} x2={width - PAD.right} y1={t.y} y2={t.y}
              stroke="var(--chart-grid)" strokeWidth="1"
            />
            <text
              x={PAD.left - 8} y={t.y + 4}
              textAnchor="end"
              className="fill-[var(--fg-subtle)] text-[10px] tabular-nums"
              style={{ direction: 'ltr' }}
            >
              {tickFormat(t.v)}
            </text>
          </g>
        ))}

        {/* تسميات المحور الأفقي — واحدة من كل بضع نقاط حتى لا تتصادم */}
        {labels.map((label, i) => {
          const every = Math.ceil(n / 7)
          if (i % every !== 0) return null
          const x = PAD.left + innerW - (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW)
          return (
            <text
              key={i} x={x} y={height - 8}
              textAnchor="middle"
              className="fill-[var(--fg-subtle)] text-[10px]"
            >
              {label}
            </text>
          )
        })}

        {/* خطّ التتبّع عند التحويم */}
        {hover !== null && (
          <line
            x1={points[0]?.coords[hover]?.x} x2={points[0]?.coords[hover]?.x}
            y1={PAD.top} y2={height - PAD.bottom}
            stroke="var(--border-strong)" strokeWidth="1"
          />
        )}

        {points.map((s) => {
          const d = s.coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ')
          const areaD = `${d} L${s.coords[s.coords.length - 1]?.x},${height - PAD.bottom} L${s.coords[0]?.x},${height - PAD.bottom} Z`
          return (
            <g key={s.key}>
              {/* التعبئة غسالة خفيفة لا كتلة مشبعة */}
              <path d={areaD} fill={s.color} opacity="0.1" />
              <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

              {/* النقطة الأخيرة معلَّمة دائماً، وبقيّتها عند التحويم فقط */}
              {s.coords.map((c, i) =>
                i === 0 || hover === i ? (
                  <circle
                    key={i} cx={c.x} cy={c.y} r="4.5"
                    fill={s.color}
                    stroke="var(--chart-surface)" strokeWidth="2"
                  />
                ) : null,
              )}
            </g>
          )
        })}
      </svg>

      {/* التلميح */}
      {hover !== null && (
        <div
          className={cn(
            'pointer-events-none absolute top-2 z-10 rounded-lg border border-border bg-surface px-3 py-2 shadow-lg',
            // التلميح يتبع نصف الرسم المقابل لموقع المؤشّر
            hover < n / 2 ? 'start-2' : 'end-2',
          )}
        >
          <p className="mb-1 text-xs font-medium text-fg">{labels[hover]}</p>
          {series.map((s) => (
            <p key={s.key} className="flex items-center gap-2 text-xs text-fg-muted">
              <span className="size-2 rounded-full" style={{ background: s.color }} aria-hidden />
              {s.label}
              <span className="ms-auto font-semibold tabular-nums text-fg" dir="ltr">
                {format(s.values[hover] ?? 0)}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
